import { useEffect, useState } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { format } from 'date-fns'
import { es as esDateFns } from 'date-fns/locale'
import { CalendarIcon, Info, PlusIcon, Volume2 } from 'lucide-react'
import { DateTime } from 'luxon'
import { es as esRdp } from 'react-day-picker/locale'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { RRule } from 'rrule'
import { v4 as uuidv4 } from 'uuid'
import { z } from 'zod'

import { Playlist } from '@/core/api/entities/playlist/playlist'
import { useTranslation } from '@/core/i18n/useTranslation'
import { RootState } from '@/core/store'
import { Button } from '@/shared/components/atoms/Button'
import { Checkbox } from '@/shared/components/atoms/Checkbox'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/shared/components/atoms/Form'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/atoms/Popover'
import { ScrollArea } from '@/shared/components/atoms/Scroll'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/atoms/Select'
import { Separator } from '@/shared/components/atoms/Separator'
import { Slider } from '@/shared/components/atoms/Slider'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/shared/components/molecules/Dialog/dialog'
import { Calendar } from '@/shared/components/organisms/Calendar/Calendar'

import { CalendarEvent, COLOR_PALETTE } from '../../context/Data'
import { useEvents } from '../../context/EventContext'
import { usePlaylistData } from '../../hooks/usePlaylistData'
import { applyOverlapResolution } from '../../utils/applyOverlapResolution'
import {
	findOverlappingEvents,
	getEventEndDate,
	getEventStartDate,
	OverlapDetails,
} from '../../utils/priorityCalculator'
import { getDaysOfWeek, toLocalISOString } from '../../utils/shared'
import { EditOverlapConfirmationModal } from '../EditOverlapConfirmationModal/EditOverlapConfirmationModal'
import { MultiOverlapWarningModal } from '../MultiOverlapWarningModal/MultiOverlapWarningModal'
import { PlaylistSelector } from '../PlaylistSelector'
import { RepeatButton } from '../RepeatButton'

const getColorFromString = (str: string): string => {
	let hash = 0
	for (let i = 0; i < str.length; i++) {
		hash = str.charCodeAt(i) + ((hash << 5) - hash)
		hash = hash & hash
	}
	const index = Math.abs(hash) % COLOR_PALETTE.length
	return COLOR_PALETTE[index]
}

const eventAddFormSchema = z
	.object({
		date: z.date({
			message: "That's not a valid date!",
		}),
		customEndDate: z.boolean(),
		endDate: z.date().optional(),
		startTime: z.string().min(1, { message: 'Select a start time' }),
		endTime: z.string().min(1, { message: 'Select an end time' }),
		repeatDays: z.array(z.string()),
		playlists: z
			.array(z.number())
			.min(1, { message: 'Select at least 1 playlist' })
			.max(2, { message: 'Select a maximum of 2 playlists' }),
		playlistPercentages: z
			.record(z.string(), z.number().min(0).max(100))
			.optional(),
		volume: z.number().min(0).max(100),
	})
	.refine(
		(data) => {
			if (data.customEndDate && !data.endDate) return false
			return true
		},
		{
			message: 'End date is required when custom end date is enabled',
			path: ['endDate'],
		}
	)

type EventAddFormValues = z.infer<typeof eventAddFormSchema>

interface EventAddFormProps {
	start: Date
	end: Date
	displayButton?: boolean
	lockTimeFields?: boolean
}

// Generate time options in 15-minute intervals
const generateTimeOptions = () => {
	const times: string[] = []
	for (let hour = 0; hour < 24; hour++) {
		for (let minute = 0; minute < 60; minute += 15) {
			const time24 = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
			times.push(time24)
		}
	}
	return times
}

export const EventAddForm = ({
	start,
	end,
	displayButton = true,
	lockTimeFields = false,
}: EventAddFormProps) => {
	const { t } = useTranslation()
	const { language } = useSelector((state: RootState) => state.ui)
	const {
		events,
		addEvent,
		updateEvent,
		deleteEvent,
		eventAddOpen,
		setEventAddOpen,
	} = useEvents()

	// Track selected playlist entities locally
	const [selectedPlaylistsMap, setSelectedPlaylistsMap] = useState<
		Record<number, Playlist>
	>({})

	// Get selected playlists as array for the hook
	const selectedPlaylists = Object.values(selectedPlaylistsMap)

	// Use shared playlist data hook with selected playlists
	const {
		allPlaylists,
		isLoading,
		isFetching,
		hasMorePages,
		loadMore,
		reset,
		searchQuery,
		setSearchQuery,
		customPlaylistIds,
	} = usePlaylistData({
		dialogOpen: eventAddOpen,
		selectedPlaylists,
	})

	// Overlap confirmation state
	const [overlapModalOpen, setOverlapModalOpen] = useState(false)
	const [overlapDetails, setOverlapDetails] = useState<OverlapDetails | null>(
		null
	)
	const [pendingEvent, setPendingEvent] = useState<CalendarEvent | null>(null)
	const [multiOverlapWarningOpen, setMultiOverlapWarningOpen] = useState(false)
	const [multiOverlapDetails, setMultiOverlapDetails] = useState<
		OverlapDetails[]
	>([])

	const daysOfWeek = getDaysOfWeek(t)

	const form = useForm<z.infer<typeof eventAddFormSchema>>({
		resolver: zodResolver(eventAddFormSchema),
		defaultValues: {
			playlists: [],
			customEndDate: false,
			repeatDays: [],
			playlistPercentages: {},
			volume: 50,
		},
	})

	useEffect(() => {
		if (eventAddOpen) {
			const startTime = `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`
			const endTime = `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`

			form.reset({
				date: start,
				customEndDate: false,
				endDate: undefined,
				startTime: startTime,
				endTime: endTime,
				repeatDays: [],
				playlists: [],
				playlistPercentages: {},
				volume: 50,
			})
			reset()
			setSelectedPlaylistsMap({})
		}
	}, [eventAddOpen, start, end, form, reset])

	/**
	 * Builds the new event from form data
	 */
	function buildNewEvent(data: EventAddFormValues): {
		newEvent: CalendarEvent
		startDate: Date
		endDate: Date
	} {
		const [startHour, startMinute] = data.startTime.split(':').map(Number)
		const [endHour, endMinute] = data.endTime.split(':').map(Number)

		const playlistsWithPercentage = data.playlists.map((id) => {
			const percentage =
				data.playlistPercentages?.[id] ||
				Math.floor(100 / data.playlists.length)
			return {
				id: id,
				percentage: percentage,
			}
		})

		const playlistNames = data.playlists
			.map((id) => allPlaylists.find((p) => p.id === id)?.title)
			.filter(Boolean) as string[]

		const playlistIds = data.playlists
			.map((id) => id.toString())
			.sort()
			.join('|')

		const color = getColorFromString(
			playlistIds || `Event ${events.length + 1}`
		)

		const slotTitle =
			playlistNames.length > 0
				? playlistNames.join(' + ')
				: t('entities.schedule.slot_form.regular_slot')

		const startDate = new Date(data.date)
		startDate.setHours(startHour, startMinute, 0, 0)

		const endDate = new Date(data.date)
		endDate.setHours(endHour, endMinute, 0, 0)

		if (endDate <= startDate) {
			endDate.setDate(endDate.getDate() + 1)
		}

		const durationMs = endDate.getTime() - startDate.getTime()

		const newEvent: any = {
			id: uuidv4(),
			title: slotTitle,
			description:
				data.repeatDays.length > 0
					? `Repeats on: ${data.repeatDays.join(', ')}`
					: 'One-time slot',
			color: color,
			backgroundColor: color,
			playlists: playlistsWithPercentage,
			repeatDays: data.repeatDays,
			volume: data.volume,
		}

		if (data.repeatDays.length > 0) {
			let rruleDays: any[] = []
			const isEveryday = data.repeatDays.includes('everyday')

			if (isEveryday) {
				rruleDays = [
					(RRule as any).MO,
					(RRule as any).TU,
					(RRule as any).WE,
					(RRule as any).TH,
					(RRule as any).FR,
					(RRule as any).SA,
					(RRule as any).SU,
				]
			} else {
				rruleDays = data.repeatDays
					.map((day) => daysOfWeek.find((d) => d.value === day)?.rruleDay)
					.filter(Boolean)
			}

			newEvent.rrule = {
				freq: (RRule as any).DAILY,
				dtstart: toLocalISOString(startDate),
			}
			// Only add byweekday if not everyday
			if (!isEveryday) {
				newEvent.rrule.byweekday = rruleDays.map((day: any) => day.weekday)
			}
			// Add until if customEndDate is set
			if (data.customEndDate && data.endDate) {
				const untilDate = new Date(data.endDate)
				// Set until to 23:59:59 of that day to include the entire day
				untilDate.setHours(23, 59, 59, 999)
				newEvent.rrule.until = toLocalISOString(untilDate)
			}

			newEvent.duration = { milliseconds: durationMs }
			newEvent.startTime = `${String(startHour).padStart(2, '0')}:${String(startMinute).padStart(2, '0')}:00`
		} else {
			newEvent.start = startDate
			newEvent.end = endDate
		}

		return { newEvent, startDate, endDate }
	}

	/**
	 * Handles confirming the overlap - update the affected slot and create the new one
	 */
	function handleOverlapConfirmUpdate() {
		if (!pendingEvent || !overlapDetails) return
		const pendingStart = getEventStartDate(pendingEvent)
		const pendingEnd = getEventEndDate(pendingEvent)
		if (!pendingStart || !pendingEnd) return

		applyOverlapResolution({
			details: overlapDetails,
			newSlotIsRecurring: (pendingEvent.repeatDays?.length ?? 0) > 0,
			newSlotStart: pendingStart,
			newSlotEnd: pendingEnd,
			updateEvent,
			addEvent,
			deleteEvent,
		})

		// Create the new event
		addEvent(pendingEvent)

		// Clear state and close modals
		setOverlapModalOpen(false)
		setOverlapDetails(null)
		setPendingEvent(null)
		setEventAddOpen(false)
	}

	/**
	 * Handles confirming the overlap - delete the completely covered slot
	 */
	function handleOverlapConfirmDelete() {
		if (!pendingEvent || !overlapDetails) return

		// Delete the completely covered slot
		deleteEvent(overlapDetails.existingEvent.id)

		// Create the new event
		addEvent(pendingEvent)

		// Clear state and close modals
		setOverlapModalOpen(false)
		setOverlapDetails(null)
		setPendingEvent(null)
		setEventAddOpen(false)
	}

	/**
	 * Handles cancelling the overlap confirmation
	 */
	function handleOverlapCancel() {
		setOverlapModalOpen(false)
		setOverlapDetails(null)
		setPendingEvent(null)
	}

	function handleBackMultiOverlap() {
		setMultiOverlapWarningOpen(false)
		setMultiOverlapDetails([])
		setPendingEvent(null)
	}

	function handleContinueMultiOverlap() {
		if (!pendingEvent) return
		if (multiOverlapDetails.length < 2) return
		const pendingStart = getEventStartDate(pendingEvent)
		const pendingEnd = getEventEndDate(pendingEvent)
		if (!pendingStart || !pendingEnd) return

		// Apply resolution for each colliding existing segment.
		// NOTE: This can delete/trim existing segments; that's why we warn.
		multiOverlapDetails.forEach((details) => {
			applyOverlapResolution({
				details,
				newSlotIsRecurring: (pendingEvent.repeatDays?.length ?? 0) > 0,
				newSlotStart: pendingStart,
				newSlotEnd: pendingEnd,
				updateEvent,
				addEvent,
				deleteEvent,
			})
		})

		addEvent(pendingEvent)

		setMultiOverlapWarningOpen(false)
		setMultiOverlapDetails([])
		setOverlapDetails(null)
		setPendingEvent(null)
		setEventAddOpen(false)
	}

	async function onSubmit(data: EventAddFormValues) {
		const { newEvent, startDate, endDate } = buildNewEvent(data)

		// Check for overlaps with existing events.
		// Include RRULE when present so bounded recurrences (until) are respected.
		const newSlotDraft = {
			start: startDate,
			end: endDate,
			repeatDays: data.repeatDays,
			rrule: (newEvent as any).rrule,
		}
		const overlaps = findOverlappingEvents(newSlotDraft as any, events)

		if (overlaps.length > 1) {
			// Multiple overlaps - warn and allow user to continue (destructive trim/delete)
			setPendingEvent(newEvent)
			setMultiOverlapDetails(overlaps)
			setMultiOverlapWarningOpen(true)
			return
		}

		if (overlaps.length === 1) {
			// Single overlap - show confirmation modal
			setPendingEvent(newEvent)
			setOverlapDetails(overlaps[0])
			setOverlapModalOpen(true)
			return
		}

		// No overlaps - create the event
		addEvent(newEvent)
		setEventAddOpen(false)
	}

	return (
		<>
			<Dialog open={eventAddOpen} onOpenChange={setEventAddOpen}>
				{displayButton && (
					<DialogTrigger className="flex" asChild>
						<Button
							className="w-24 md:w-28 text-xs md:text-sm"
							variant="default"
							onClick={() => setEventAddOpen(true)}
						>
							<PlusIcon className="md:h-5 md:w-5 h-3 w-3" />
							<p>Add Event</p>
						</Button>
					</DialogTrigger>
				)}
				<DialogContent
					aria-describedby={undefined}
					scrollable={false}
					className="max-w-2xl h-auto p-0 gap-0"
				>
					<DialogHeader className="px-6 pt-6 pb-4">
						<DialogTitle className="text-xl">
							{t('entities.schedule.slot_form.set_up_regular_slot')}
						</DialogTitle>
					</DialogHeader>

					<Separator />

					<Form {...form}>
						<form
							onSubmit={form.handleSubmit(onSubmit)}
							className="flex flex-col"
						>
							{/* Date and Time Section */}
							<div className="px-6 py-4 space-y-3">
								{/* Date and Time in one row */}
								<div className="flex items-center gap-2 text-sm">
									{/* Date Picker */}
									<FormField
										control={form.control}
										name="date"
										render={({ field }) => (
											<FormItem>
												<Popover>
													<PopoverTrigger asChild disabled={lockTimeFields}>
														<FormControl>
															<Button
																variant="ghost"
																className={`h-auto p-0 hover:bg-transparent font-normal ${lockTimeFields ? 'cursor-not-allowed opacity-70' : ''}`}
																disabled={lockTimeFields}
															>
																{field.value ? (
																	format(field.value, 'EEEE, d MMM', {
																		locale: language?.startsWith('es')
																			? esDateFns
																			: undefined,
																	})
																) : (
																	<span>
																		{t(
																			'entities.schedule.slot_form.pick_a_date'
																		)}
																	</span>
																)}
															</Button>
														</FormControl>
													</PopoverTrigger>
													<PopoverContent className="w-auto p-0" align="start">
														<Calendar
															mode="single"
															selected={field.value}
															onSelect={field.onChange}
															autoFocus
															locale={
																language?.startsWith('es') ? esRdp : undefined
															}
														/>
													</PopoverContent>
												</Popover>
												<FormMessage />
											</FormItem>
										)}
									/>

									<span className="text-muted-foreground">
										{t('entities.schedule.slot_form.from').toLowerCase()}
									</span>

									{/* Start Time */}
									<FormField
										control={form.control}
										name="startTime"
										render={({ field }) => (
											<FormItem>
												<Select
													onValueChange={field.onChange}
													defaultValue={field.value}
													value={field.value}
													disabled={lockTimeFields}
												>
													<FormControl>
														<SelectTrigger
															className={`h-auto px-2 py-1 w-auto border-0 bg-muted/50 ${lockTimeFields ? 'cursor-not-allowed opacity-70' : ''}`}
														>
															<SelectValue placeholder="Start">
																{field.value &&
																	DateTime.fromFormat(
																		field.value,
																		'HH:mm'
																	).toLocaleString(DateTime.TIME_SIMPLE)}
															</SelectValue>
														</SelectTrigger>
													</FormControl>
													<SelectContent>
														<ScrollArea className="h-60">
															{generateTimeOptions().map((time) => (
																<SelectItem key={time} value={time}>
																	{DateTime.fromFormat(
																		time,
																		'HH:mm'
																	).toLocaleString(DateTime.TIME_SIMPLE)}
																</SelectItem>
															))}
														</ScrollArea>
													</SelectContent>
												</Select>
												<FormMessage />
											</FormItem>
										)}
									/>

									<span className="text-muted-foreground">
										{t('entities.schedule.slot_form.until').toLowerCase()}
									</span>

									{/* End Time */}
									<FormField
										control={form.control}
										name="endTime"
										render={({ field }) => (
											<FormItem>
												<Select
													onValueChange={field.onChange}
													defaultValue={field.value}
													value={field.value}
													disabled={lockTimeFields}
												>
													<FormControl>
														<SelectTrigger
															className={`h-auto px-2 py-1 w-auto border-0 bg-muted/50 ${lockTimeFields ? 'cursor-not-allowed opacity-70' : ''}`}
														>
															<SelectValue placeholder="End">
																{field.value &&
																	DateTime.fromFormat(
																		field.value,
																		'HH:mm'
																	).toLocaleString(DateTime.TIME_SIMPLE)}
															</SelectValue>
														</SelectTrigger>
													</FormControl>
													<SelectContent>
														<ScrollArea className="h-60">
															{generateTimeOptions().map((time) => (
																<SelectItem key={time} value={time}>
																	{DateTime.fromFormat(
																		time,
																		'HH:mm'
																	).toLocaleString(DateTime.TIME_SIMPLE)}
																</SelectItem>
															))}
														</ScrollArea>
													</SelectContent>
												</Select>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>

								{/* Second row: Checkbox and Repeat button */}
								<div className="flex flex-row items-center justify-between gap-2">
									{/* Repeat Button */}
									<FormField
										control={form.control}
										name="repeatDays"
										render={({ field }) => (
											<FormItem>
												<RepeatButton
													value={field.value}
													onChange={field.onChange}
												/>
												<FormMessage />
											</FormItem>
										)}
									/>

									{/* Custom End Date Checkbox - only shown when repeat is activated */}
									{form.watch('repeatDays').length > 0 && (
										<FormField
											control={form.control}
											name="customEndDate"
											render={({ field }) => (
												<FormItem className="flex flex-row items-center space-x-2 space-y-0">
													<FormControl>
														<Checkbox
															checked={field.value}
															onCheckedChange={field.onChange}
														/>
													</FormControl>
													<FormLabel className="text-sm font-normal cursor-pointer">
														{t(
															'entities.schedule.slot_form.choose_custom_end_date'
														)}
													</FormLabel>
												</FormItem>
											)}
										/>
									)}
								</div>

								{/* End Date Picker (shown when custom end date is checked) */}
								{form.watch('customEndDate') && (
									<FormField
										control={form.control}
										name="endDate"
										render={({ field }) => (
											<FormItem className="flex flex-col">
												<Popover>
													<PopoverTrigger asChild>
														<FormControl>
															<Button
																variant="outline"
																className="w-full justify-start text-left font-normal h-9 text-sm"
															>
																<CalendarIcon className="mr-2 h-4 w-4" />
																{field.value ? (
																	format(field.value, 'EEEE, d MMM', {
																		locale: language?.startsWith('es')
																			? esDateFns
																			: undefined,
																	})
																) : (
																	<span>
																		{t(
																			'entities.schedule.slot_form.pick_a_date'
																		)}
																	</span>
																)}
															</Button>
														</FormControl>
													</PopoverTrigger>
													<PopoverContent className="w-auto p-0" align="start">
														<Calendar
															mode="single"
															selected={field.value}
															onSelect={field.onChange}
															autoFocus
															locale={
																language?.startsWith('es') ? esRdp : undefined
															}
														/>
													</PopoverContent>
												</Popover>
												<FormMessage />
											</FormItem>
										)}
									/>
								)}
							</div>

							<Separator />

							{/* Playlist Search and Selection */}
							<div className="px-6 py-4">
								<FormField
									control={form.control}
									name="playlists"
									render={({ field }) => (
										<FormItem>
											<PlaylistSelector
												value={field.value}
												onChange={field.onChange}
												onPercentagesChange={(percentages) =>
													form.setValue('playlistPercentages', percentages)
												}
												onPlaylistsChange={(playlists) => {
													const newMap: Record<number, Playlist> = {}
													playlists.forEach((p) => {
														newMap[p.id] = p
													})
													setSelectedPlaylistsMap(newMap)
												}}
												playlists={allPlaylists}
												isLoading={isLoading || isFetching}
												hasMorePages={hasMorePages}
												onLoadMore={loadMore}
												dialogOpen={eventAddOpen}
												searchQuery={searchQuery}
												onSearchQueryChange={setSearchQuery}
												customPlaylistIds={customPlaylistIds}
											/>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							{/* Playback Percentage (only shown when 2 playlists selected) */}
							{form.watch('playlists').length === 2 && (
								<div className="px-6 pb-3">
									<div className="space-y-2">
										<div className="flex items-center gap-2">
											<h4 className="text-sm font-medium">
												{t('entities.schedule.slot_form.playback_percentage')}
											</h4>
											<Info className="h-3 w-3 text-muted-foreground" />
										</div>
										<div className="flex gap-2">
											{form.watch('playlists').map((playlistId) => {
												const playlist = allPlaylists.find(
													(p) => p.id === playlistId
												)
												if (!playlist) return null

												const currentPercentages =
													form.watch('playlistPercentages') || {}
												const percentage = currentPercentages[playlistId] || 50

												return (
													<div
														key={playlistId}
														className="flex-1 flex items-center gap-2 px-2 py-1.5 border rounded-lg bg-muted/50"
													>
														<span
															className="text-xs font-medium flex-1 truncate max-w-[100px]"
															title={playlist.title}
														>
															{playlist.title}
														</span>
														{/**
														 * <button
															type="button"
															onClick={() => {
																const newPercentages = { ...currentPercentages }
																const otherPlaylistId = form
																	.watch('playlists')
																	.find((id) => id !== playlistId)
																const newValue = Math.max(0, percentage - 10)
																newPercentages[playlistId] = newValue
																if (otherPlaylistId) {
																	newPercentages[otherPlaylistId] =
																		100 - newValue
																}
																form.setValue(
																	'playlistPercentages',
																	newPercentages
																)
															}}
															disabled={true}
															className="h-5 w-5 rounded-full border flex items-center justify-center hover:bg-background transition-colors flex-shrink-0 cursor-not-allowed"
														>
															<Minus className="h-3 w-3" />
														</button>
														 */}
														<span className="text-xs font-semibold w-10 text-center flex-shrink-0">
															{percentage}%
														</span>
														{/**
														 * <button
															type="button"
															onClick={() => {
																const newPercentages = { ...currentPercentages }
																const otherPlaylistId = form
																	.watch('playlists')
																	.find((id) => id !== playlistId)
																const newValue = Math.min(100, percentage + 10)
																newPercentages[playlistId] = newValue
																if (otherPlaylistId) {
																	newPercentages[otherPlaylistId] =
																		100 - newValue
																}
																form.setValue(
																	'playlistPercentages',
																	newPercentages
																)
															}}
															disabled={true}
															className="h-5 w-5 rounded-full border flex items-center justify-center hover:bg-background transition-colors flex-shrink-0 cursor-not-allowed"
														>
															<Plus className="h-3 w-3" />
														</button>
														 */}
													</div>
												)
											})}
										</div>
									</div>
								</div>
							)}

							{/* Playlist Volume (always visible) */}
							<div className="px-6 pb-4" style={{ display: 'none' }}>
								{form.watch('playlists').length === 2 && (
									<Separator className="mb-3" />
								)}
								<FormField
									control={form.control}
									name="volume"
									render={({ field }) => (
										<FormItem className="space-y-2">
											<div className="flex items-center gap-2">
												<FormLabel className="text-sm font-medium">
													{t('entities.schedule.slot_form.playlist_volume')}
												</FormLabel>
												<Info className="h-3 w-3 text-muted-foreground" />
											</div>
											<div className="flex border rounded-sm p-2 items-center justify-between gap-3">
												<Volume2 className="h-5 w-5 text-muted-foreground flex-shrink-0" />
												<FormControl>
													<Slider
														value={[field.value]}
														onValueChange={(value) => field.onChange(value[0])}
														max={100}
														min={0}
														step={1}
														className="flex-1"
													/>
												</FormControl>
												<span className="text-sm font-semibold w-8 text-right flex-shrink-0">
													{field.value}
												</span>
											</div>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<Separator />

							{/* Footer Buttons */}
							<DialogFooter className="px-6 py-4">
								<Button
									type="button"
									variant="outline"
									onClick={() => setEventAddOpen(false)}
									className="h-8 rounded-full px-6"
								>
									{t('actions.back')}
								</Button>
								<Button type="submit" className="h-8 rounded-full px-6">
									{t('actions.continue')}
								</Button>
							</DialogFooter>
						</form>
					</Form>
				</DialogContent>
			</Dialog>

			{/* Multiple Overlaps Warning Modal */}
			<MultiOverlapWarningModal
				open={multiOverlapWarningOpen}
				onOpenChange={(open) => {
					setMultiOverlapWarningOpen(open)
					if (!open) handleBackMultiOverlap()
				}}
				overlapCount={multiOverlapDetails.length}
				onBack={handleBackMultiOverlap}
				onContinue={handleContinueMultiOverlap}
			/>

			{/* Overlap Confirmation Modal */}
			<EditOverlapConfirmationModal
				open={overlapModalOpen}
				onOpenChange={setOverlapModalOpen}
				overlapDetails={overlapDetails}
				editingEventTitle={pendingEvent?.title || 'New Slot'}
				onConfirmUpdate={handleOverlapConfirmUpdate}
				onConfirmDelete={handleOverlapConfirmDelete}
				onCancel={handleOverlapCancel}
			/>
		</>
	)
}
